import { Storage } from "aws-amplify";
import { useEffect, useState } from "react";

interface ProfilePictureData {
  key: string;
  url: string;
  expiredTimeStamp: number;
}

interface IUploadFile {
  key: string;
  file: any;
  onStart?: () => void;
  onFinish?: (key: string) => void;
  onError?: (error: any) => void;
  contentType?: string;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  compressionEnabled?: boolean;
  public?: boolean; // Make the file publicly accessible
}

/**
 * Compresses an image file by resizing and encoding to specified quality
 * @param file - The image file to compress
 * @param maxWidth - Maximum width (default 1200px)
 * @param maxHeight - Maximum height (default 1200px)
 * @param quality - JPEG quality (0-1, default 0.9)
 * @returns Promise resolving to a compressed Blob
 */
export const compressImage = async (
  file: File,
  maxWidth: number = 1200,
  maxHeight: number = 1200,
  quality: number = 0.9
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    // Skip compression for non-image files
    if (!file.type.startsWith("image/")) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        // Calculate new dimensions while preserving aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round(height * (maxWidth / width));
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round(width * (maxHeight / height));
            height = maxHeight;
          }
        }

        // Create canvas and draw resized image
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob
        const mimeType = file.type || "image/jpeg";
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Canvas to Blob conversion failed"));
              return;
            }

            const compressedFile = new File([blob], file.name, {
              type: mimeType,
              lastModified: Date.now(),
            });

            console.log(`Image compressed: ${(file.size / 1024).toFixed(2)}KB → ${(blob.size / 1024).toFixed(2)}KB`);
            resolve(compressedFile);
          },
          mimeType,
          quality
        );
      };
      img.onerror = () => {
        reject(new Error("Error loading image"));
      };
    };
    reader.onerror = () => {
      reject(new Error("Error reading file"));
    };
  });
};

export const uploadFile = async (payload: IUploadFile) => {
  try {
    if (payload.onStart) payload.onStart();

    const dataFile: File = payload.file?.originFileObj || payload.file;
    let fileToUpload: File | Blob = dataFile;

    // Compress image if it's an image file and compression is not explicitly disabled
    if (
      dataFile.type.startsWith("image/") &&
      payload.compressionEnabled !== false
    ) {
      fileToUpload = await compressImage(
        dataFile,
        payload.maxWidth || 1200,
        payload.maxHeight || 1200,
        payload.quality || 0.9
      );
    }

    await Storage.put(payload.key, fileToUpload, {
      contentType: payload.contentType || dataFile?.type,
    });

    if (payload.onFinish) payload.onFinish(payload.key);
  } catch (error) {
    console.error("Error upload file URL:", error);
    if (payload.onError) payload.onError(error);
  }
};

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    // Increment the database version to 2 to trigger the onupgradeneeded event.
    // This is necessary to create the profilePictures object store for users who have an older version of the database.
    const request = indexedDB.open("profilePicturesDB", 2);

    request.onerror = function (event) {
      reject(
        new Error(
          "IndexedDB error: " + (event.target as IDBOpenDBRequest).error
        )
      );
    };

    request.onsuccess = function (event) {
      resolve((event.target as IDBOpenDBRequest).result as IDBDatabase);
    };

    request.onupgradeneeded = function (event) {
      const db = (event.target as IDBOpenDBRequest).result as IDBDatabase;
      // Create the profilePictures object store only if it doesn't already exist.
      if (!db.objectStoreNames.contains("profilePictures")) {
        db.createObjectStore("profilePictures", { keyPath: "key" }).createIndex(
          "expiredTimeStamp",
          "expiredTimeStamp",
          { unique: false }
        );
      }
    };
  });
};

const getProfilePictureFromDB = async (
  key: string,
): Promise<ProfilePictureData | undefined> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(["profilePictures"], "readonly");
    const objectStore = transaction.objectStore("profilePictures");
    const getRequest = objectStore.get(key);

    getRequest.onerror = function (event) {
      reject(
        new Error("Error fetching data: " + (event.target as IDBRequest).error),
      );
    };

    getRequest.onsuccess = function (event) {
      resolve((event.target as IDBRequest).result as ProfilePictureData);
    };
  });
};

const saveProfilePictureToDB = async (
  key: string,
  profilePicture: string,
): Promise<string> => {
  const db = await openDB();
  const currentTime = Date.now();
  const expiredTimeStamp = currentTime + 55 * 60 * 1000; // 55 minutes in milliseconds, since the max amplify expires time is 1 hour
  const data: ProfilePictureData = {
    key,
    url: profilePicture,
    expiredTimeStamp,
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(["profilePictures"], "readwrite");
    const objectStore = transaction.objectStore("profilePictures");
    const request = objectStore.put(data);

    request.onerror = function (event) {
      reject(
        new Error("Error saving data: " + (event.target as IDBRequest).error),
      );
    };

    request.onsuccess = function () {
      resolve(profilePicture);
    };
  });
};

export const getProfilePicture = async (key: string): Promise<string> => {
  const profilePictureData = await getProfilePictureFromDB(key);

  if (!profilePictureData || Date.now() > profilePictureData.expiredTimeStamp) {
    // expires set to 3600  => 1 hour in seconds, the max expires based on docs https://docs.amplify.aws/gen1/nextjs/prev/build-a-backend/storage/downloa
    const profilePicture = await Storage.get(key, { expires: 3600 });
    await saveProfilePictureToDB(key, profilePicture);
    return profilePicture;
  }

  return profilePictureData.url;
};

export const getFileURLWithCache = async (
  key: string,
): Promise<string | null> => {
  try {
    return getProfilePicture(key);
  } catch (error) {
    console.error("Error fetching file URL:", error);
    return null; // or handle the error in a way that makes sense for your application
  }
};

export const getFileURL = async (key: string): Promise<string | null> => {
  try {
    return await Storage.get(key, { expires: 3600 });
  } catch (error) {
    console.error("Error fetching file URL:", error);
    return null; // or handle the error in a way that makes sense for your application
  }
};

export const getFileURLWithSize = async (key: string): Promise<{
  url: string;
  size: number | null;
} | null> => {
  try {
    return {
      url: await Storage.get(key, { expires: 3600 }),
      size: Math.round(Number((await Storage.getProperties(key)).contentLength) / 1024 / 1024)
    }
  } catch (error) {
    console.error("Error fetching file URL:", error);
    return null; // or handle the error in a way that makes sense for your application
  }
};

export const useGetFileURL = (path: string, withSize?: boolean) => {
  const [url, setUrl] = useState<string>("");
  const [keyUrl, setKeyUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [fileSize, setFileSize] = useState<number | null>(null);

  const setLoadingAndUrl = (newUrl: string) => {
    if (newUrl !== url) {
      setUrl(newUrl);
      setIsLoading(false);
    }
  };

  const getFileUrlIfNeeded = async (key: string) => {
    if (
      typeof key === "string" &&
      (key.startsWith("http://") || key.startsWith("https://"))
    ) {
      setLoadingAndUrl(key);
    } else if (key !== keyUrl) {
      // Check if key is different from keyUrl
      try {
        setIsError(false);
        if (withSize) {
          const urlResWSize = await getFileURLWithSize(key);
          setLoadingAndUrl(urlResWSize?.url as string);
          setFileSize(urlResWSize?.size || null);
        } else {
          const urlRes = await getFileURL(key);
          setLoadingAndUrl(urlRes as string);
        }
        setKeyUrl(key); // Update keyUrl only when new URL is fetched
      } catch (error) {
        console.error("Error getFileURL file URL:", error);
        setIsError(true);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        await getFileUrlIfNeeded(path);
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsError(true);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    if (path && keyUrl !== path) {
      // Check if keyUrl is different from path and URL is empty
      fetchData();
    } else {
      setIsLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [path, keyUrl, url]);

  return { isLoading, url, isError, getFileUrlIfNeeded, setUrl, fileSize };
};